// In local dev VITE_API_URL is typically unset and the Vite dev-server proxies
// "/api" and "/health" to the .NET backend (see vite.config.ts). In production
// the frontend and backend are separate Cloud Run services, so VITE_API_URL is
// baked in at build time to point at the backend service URL.
import type { CalcInputs, ProjectionSummary } from "./atomika-calc";
import type {
  CustomerListResult,
  DashboardData,
  DashboardStatusData,
} from "./atomika-dashboard";
import type { OverviewData } from "./atomika-overview";
import type {
  ActivateCustomerPayload,
  ApiKeyCreated,
  ApiKeyInfo,
  CompanyDetail,
  CompanySearchResult,
  CreateApiKeyPayload,
  CreateOpportunityPayload,
  MarkadurCustomerListResult,
  Opportunity,
  Pipeline,
  StagesConfig,
  UpdateOpportunityPayload,
} from "./markadur";

const API_URL: string = import.meta.env.VITE_API_URL ?? "";

export interface Todo {
  id: number;
  title: string;
  isComplete: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface Scenario {
  id: number;
  customerName: string;
  notes: string | null;
  inputs: CalcInputs;
  results: ProjectionSummary;
  createdAt: string;
  updatedAt: string | null;
}

export interface SaveScenarioPayload {
  customerName: string;
  notes?: string | null;
  inputs: CalcInputs;
  results: ProjectionSummary;
}

export interface HealthStatus {
  status: string;
  time: string;
}

const TOKEN_KEY = "atomika-token";

export type UserRole = "superadmin" | "admin" | "user";

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  displayName: string | null;
  areaIds: string[] | "*";
  mustChangePassword: boolean;
}

export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  role: UserRole;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  areaIds: string[] | "*";
}

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function hasAuth(): boolean {
  return Boolean(getToken());
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const UNAUTHORIZED_EVENT = "atomika:unauthorized";

function handleUnauthorized(path: string): void {
  if (path.startsWith("/api/auth/login")) return;
  clearAuth();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }
}

async function extractError(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) return body.error;
  } catch {
    // response had no JSON body
  }
  return `${fallback} (${res.status})`;
}

async function guard(
  res: Response,
  path: string,
  fallback: string,
): Promise<void> {
  if (res.ok) return;
  if (res.status === 401) handleUnauthorized(path);
  throw new Error(await extractError(res, fallback));
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { ...authHeaders() },
  });
  await guard(res, path, `Request to ${path} failed`);
  return (await res.json()) as T;
}

async function sendJson<T>(
  path: string,
  method: "POST" | "PATCH" | "PUT",
  body: unknown,
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  await guard(res, path, `Request to ${path} failed`);
  return (await res.json()) as T;
}

async function deleteReq(path: string, fallback: string): Promise<void> {
  const res = await fetch(`${API_URL}${path}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  });
  await guard(res, path, fallback);
}

export const api = {
  health: () => getJson<HealthStatus>("/health"),

  listTodos: () => getJson<Todo[]>("/api/todos"),

  createTodo: (title: string): Promise<Todo> =>
    sendJson<Todo>("/api/todos", "POST", { title }),

  updateTodo: (
    id: number,
    changes: { title: string; isComplete: boolean },
  ): Promise<Todo> => sendJson<Todo>(`/api/todos/${id}`, "PUT", changes),

  deleteTodo: (id: number): Promise<void> =>
    deleteReq(`/api/todos/${id}`, "Failed to delete todo"),

  listScenarios: () => getJson<Scenario[]>("/api/atomika/scenarios"),

  saveScenario: (payload: SaveScenarioPayload): Promise<Scenario> =>
    sendJson<Scenario>("/api/atomika/scenarios", "POST", payload),

  deleteScenario: (id: number): Promise<void> =>
    deleteReq(`/api/atomika/scenarios/${id}`, "Failed to delete scenario"),

  getDashboard: (
    params: {
      from?: string;
      to?: string;
      source?: string;
      companyId?: string;
    } = {},
  ): Promise<DashboardData> => {
    const qs = new URLSearchParams();
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.source) qs.set("source", params.source);
    if (params.companyId) qs.set("companyId", params.companyId);
    const query = qs.toString();
    return getJson<DashboardData>(
      `/api/atomika/dashboard${query ? `?${query}` : ""}`,
    );
  },

  getDashboardStatus: (companyId?: string): Promise<DashboardStatusData> =>
    getJson<DashboardStatusData>(
      `/api/atomika/status${companyId ? `?companyId=${encodeURIComponent(companyId)}` : ""}`,
    ),

  getCustomers: (): Promise<CustomerListResult> =>
    getJson<CustomerListResult>("/api/atomika/customers"),

  getOverview: (): Promise<OverviewData> =>
    getJson<OverviewData>("/api/atomika/overview"),

  getMarkadurStages: (): Promise<StagesConfig> =>
    getJson<StagesConfig>("/api/markadur/stages"),

  searchCompanies: (q: string, limit = 25): Promise<CompanySearchResult> => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    qs.set("limit", String(limit));
    return getJson<CompanySearchResult>(`/api/markadur/companies?${qs}`);
  },

  getCompany: (kennitala: string): Promise<CompanyDetail> =>
    getJson<CompanyDetail>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}`,
    ),

  getPipeline: (org?: string): Promise<Pipeline> =>
    getJson<Pipeline>(
      `/api/markadur/pipeline${org ? `?org=${encodeURIComponent(org)}` : ""}`,
    ),

  createOpportunity: (
    payload: CreateOpportunityPayload,
  ): Promise<Opportunity> =>
    sendJson<Opportunity>("/api/markadur/opportunities", "POST", payload),

  updateOpportunity: (
    id: number,
    payload: UpdateOpportunityPayload,
  ): Promise<Opportunity> =>
    sendJson<Opportunity>(
      `/api/markadur/opportunities/${id}`,
      "PATCH",
      payload,
    ),

  deleteOpportunity: (id: number): Promise<void> =>
    deleteReq(
      `/api/markadur/opportunities/${id}`,
      "Failed to delete opportunity",
    ),

  getMarkadurCustomers: (
    activeOnly = false,
  ): Promise<MarkadurCustomerListResult> =>
    getJson<MarkadurCustomerListResult>(
      `/api/markadur/customers${activeOnly ? "?activeOnly=true" : ""}`,
    ),

  activateCustomer: (
    kennitala: string,
    payload: ActivateCustomerPayload = {},
  ): Promise<{ activated: boolean; kennitala: string }> =>
    sendJson(
      `/api/markadur/customers/${encodeURIComponent(kennitala)}/activate`,
      "POST",
      payload,
    ),

  deactivateCustomer: (kennitala: string): Promise<void> =>
    deleteReq(
      `/api/markadur/customers/${encodeURIComponent(kennitala)}/activate`,
      "Failed to deactivate customer",
    ),

  listApiKeys: (customerKennitala?: string): Promise<ApiKeyInfo[]> =>
    getJson<ApiKeyInfo[]>(
      `/api/api-keys${customerKennitala ? `?customerKennitala=${encodeURIComponent(customerKennitala)}` : ""}`,
    ),

  createApiKey: (payload: CreateApiKeyPayload): Promise<ApiKeyCreated> =>
    sendJson<ApiKeyCreated>("/api/api-keys", "POST", payload),

  revokeApiKey: (id: number): Promise<ApiKeyInfo> =>
    sendJson<ApiKeyInfo>(`/api/api-keys/${id}/revoke`, "POST", {}),

  deleteApiKey: (id: number): Promise<void> =>
    deleteReq(`/api/api-keys/${id}`, "Failed to delete API key"),

  login: (
    email: string,
    password: string,
  ): Promise<{ token: string; user: AuthUser }> =>
    sendJson<{ token: string; user: AuthUser }>("/api/auth/login", "POST", {
      email,
      password,
    }),

  logout: (): Promise<{ ok: true }> =>
    sendJson<{ ok: true }>("/api/auth/logout", "POST", {}),

  forgotPassword: (email: string): Promise<{ ok: true }> =>
    sendJson<{ ok: true }>("/api/auth/forgot-password", "POST", { email }),

  me: (): Promise<{ user: AuthUser }> =>
    getJson<{ user: AuthUser }>("/api/auth/me"),

  changePassword: (
    currentPassword: string,
    newPassword: string,
  ): Promise<{ ok: true; token: string }> =>
    sendJson<{ ok: true; token: string }>("/api/auth/change-password", "POST", {
      currentPassword,
      newPassword,
    }),

  admin: {
    listUsers: (): Promise<{ users: AdminUser[] }> =>
      getJson<{ users: AdminUser[] }>("/api/admin/users"),

    createUser: (body: {
      email: string;
      displayName?: string;
      password: string;
      role: UserRole;
      areaIds?: string[];
    }): Promise<{ id: string }> =>
      sendJson<{ id: string }>("/api/admin/users", "POST", body),

    updateUser: (
      id: string,
      body: {
        displayName?: string;
        isActive?: boolean;
        role?: UserRole;
        password?: string;
      },
    ): Promise<{ ok: true }> =>
      sendJson<{ ok: true }>(`/api/admin/users/${id}`, "PATCH", body),

    setAccess: (id: string, areaIds: string[]): Promise<{ ok: true }> =>
      sendJson<{ ok: true }>(`/api/admin/users/${id}/access`, "PUT", {
        areaIds,
      }),

    sendCredentials: (
      id: string,
    ): Promise<{
      email: string;
      tempPassword: string;
      loginUrl: string;
      emailSent: boolean;
    }> => sendJson(`/api/admin/users/${id}/send-credentials`, "POST", {}),

    deleteUser: (id: string): Promise<void> =>
      deleteReq(`/api/admin/users/${id}`, "Failed to delete user"),
  },
};
